import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Square, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone, Building2, Sofa } from 'lucide-react';

const CommercialWindowCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "Do you clean windows on multi-storey buildings?",
      a: "Yes. Most jobs are handled with water-fed pole systems, which can reach several storeys from ground level. For taller buildings, we hire scissor lifts suited to the job, so we can safely access upper levels without being limited to ground-floor work."
    },
    {
      q: "What equipment do you use for commercial window cleaning?",
      a: "We primarily use water-fed pole systems, which deliver purified water for a streak-free finish without the need for ladders on most jobs. For buildings beyond pole reach, we hire scissor lifts to safely access upper levels."
    },
    {
      q: "Do you clean both interior and exterior windows?",
      a: "Yes, we clean both interior and exterior windows as part of a commercial window clean. Scope is built around your property and what you need done."
    },
    {
      q: "How often should commercial windows be cleaned?",
      a: "It depends on the property and how quickly windows get dirty from dust, weather or traffic. We can recommend a schedule based on your site, whether that's monthly, quarterly or as needed."
    }
  ];

  const relatedServices = [
    {
      title: 'Commercial Office Cleaning',
      description: 'Daily, weekly or monthly office cleaning, including windows as part of a full clean.',
      link: '/services/commercial-office-cleaning-perth',
      icon: Building2
    },
    {
      title: 'Commercial Carpet Cleaning',
      description: 'Deep steam or dry carpet cleaning for office and commercial floors.',
      link: '/services/commercial-carpet-cleaning-perth',
      icon: Sofa
    }
  ];

  // Service Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Commercial Window Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "Osborne Park", "Malaga", "Kewdale", "Welshpool", "Canning Vale", 
      "Bibra Lake", "Jandakot", "Wangara", "Balcatta", "Bayswater", "Rockingham", 
      "Mandurah", "Joondalup", "Midland", "Yanchep", "Leederville", "Northbridge", 
      "Mount Lawley", "Fremantle", "Cockburn", "Landsdale", "Hope Valley", 
      "Kalgoorlie", "Esperance", "Albany", "Busselton", "Bunbury", "Margaret River", "Australind", "Geraldton"
    ],
    "description": "Streak-free window cleaning for commercial properties, from ground level to multi-storey. Water-fed pole systems and scissor lift hire for taller buildings."
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/services/commercial-window-cleaning-perth" />
        <title>Commercial Window Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="Streak-free commercial window cleaning across Perth and WA. Water-fed pole systems and scissor lift hire for multi-storey buildings. 20+ years experience." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="commercial-window-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1482449609509-eae2a7ea42b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwd2luZG93JTIwY2xlYW5pbmclMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc4OTM1MzI1Nnww&ixlib=rb-4.1.0&q=85"
              alt="Window cleaner on scissor lift cleaning commercial building glass"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <Square size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Window Cleaning</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Commercial Window Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Streak-free window cleaning for commercial properties, from ground level to multi-storey
              </p>
              <p className="text-gray-400 mb-8">
                Clean windows make a real difference to how a commercial property presents, and they're often one of the first things a client or visitor notices. Extreme Clean Australia provides commercial window cleaning across Perth and WA, using water-fed pole systems for reach and safety, with scissor lift hire available for taller or harder-to-access buildings.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                Interior and exterior window cleaning for commercial properties of any size, using water-fed pole systems for a streak-free finish without the need for ladders on most jobs. For taller buildings or hard-to-reach areas, we hire scissor lifts to safely access upper levels. Scope is built around your property, whether that's a single-storey office or a larger multi-storey commercial building.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Interior window cleaning',
                  'Exterior window cleaning',
                  'Water-fed pole systems',
                  'Scissor lift hire for height',
                  'Streak-free finish',
                  'Single & multi-storey buildings'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Ground Level to Multi-Storey */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Reach</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Ground Level to Multi-Storey</h2>
              <p className="text-gray-600">
                Most commercial window cleaning is handled efficiently with water-fed pole systems, reaching several storeys without needing to work at height. For buildings beyond that reach, we hire scissor lifts suited to the job, giving us the flexibility to take on properties of different heights and access requirements, rather than being limited to ground-level work.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Why Us</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Why Businesses Choose Extreme Clean Australia</h2>
              <p className="text-gray-300">
                Streak-free results and the right equipment for the job matter, but so does reliability. Backed by 20+ years in commercial and industrial cleaning across WA, we bring the same standard to window cleaning as we do to compliance-critical kitchen exhaust work, showing up on schedule and getting it done properly.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Perth Metro</h3>
                  <p className="text-gray-600">
                    We service the entire Perth metro area, from Yanchep in the north to Mandurah in the south, including Osborne Park, Malaga, Kewdale, Welshpool, Canning Vale, Bibra Lake, Jandakot, Wangara, Balcatta, Bayswater, Rockingham, Joondalup and Midland.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Regional WA</h3>
                  <p className="text-gray-600">
                    Kalgoorlie, Esperance, Albany, Busselton, Bunbury, Margaret River, Australind, Geraldton.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Related Services</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedServices.map((service, index) => (
                  <Link 
                    key={index}
                    to={service.link}
                    className="block bg-gray-50 p-6 border border-gray-200 hover:border-[#3B82F6] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-blue-50 mb-4 flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                      <service.icon size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-[#3B82F6] transition-colors">{service.title}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your Window Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for commercial window cleaning, from single-storey to multi-level buildings.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CommercialWindowCleaning;
